import React, { useContext, useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import MicIcon from "@material-ui/icons/Mic";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import ShortTextIcon from "@material-ui/icons/ShortText";
import MessageIcon from "@material-ui/icons/Message";
import CodeIcon from "@material-ui/icons/Code";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import VisibilityIcon from "@material-ui/icons/Visibility";
import IconButton from "@material-ui/core/IconButton";
import { i18n } from "../../translate/i18n";
import { head } from "lodash";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessageVariablesPicker from "../MessageVariablesPicker";
import { DataGrid } from "@material-ui/data-grid";
import AudioRecorder from "../AudioRecorder";

import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Box,
  Typography,
  Chip,
  Divider
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  dialogPaper: {
    borderRadius: 12,
    overflow: "hidden",
    maxWidth: 760,
    width: "100%",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  dialogTitle: {
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${
      theme.palette.primary.dark || theme.palette.primary.main
    } 100%)`,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1.5, 2),
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: "1rem",
    fontWeight: 600,
  },
  dialogTitleIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.2)",
  },
  dialogContent: {
    padding: theme.spacing(2, 2.5, 1.5),
    overflowY: "auto",
    flex: "1 1 auto",
    maxHeight: "calc(90vh - 120px)",
  },
  tabs: {
    minHeight: 40,
    marginBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tab: {
    minHeight: 40,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.85rem",
    fontWeight: 700,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  sectionIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.05rem",
  },
  sectionDivider: {
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(1.5, 0, 1.25),
  },
  fieldDense: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: "0.875rem",
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.875rem",
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 14px",
      boxSizing: "border-box",
    },
    "& .MuiSelect-outlined": {
      padding: "12px 14px",
      boxSizing: "border-box",
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  sectionCard: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    backgroundColor: theme.palette.action.hover,
    marginBottom: theme.spacing(0.5),
  },
  mediaOptions: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-start",
    flexWrap: "wrap",
  },
  mediaOptionBtn: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
  },
  mediaInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 1.25),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  existingMediaActions: {
    display: "flex",
    gap: theme.spacing(0.5),
    alignItems: "center",
  },
  mediaPreview: {
    maxWidth: "100%",
    maxHeight: 200,
    borderRadius: 8,
    marginTop: theme.spacing(1),
  },
  dialogActions: {
    padding: theme.spacing(1.25, 2),
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,
    gap: 8,
    flexShrink: 0,
  },
  cancelBtn: {
    textTransform: "none",
    borderRadius: 8,
    fontWeight: 600,
  },
  saveBtn: {
    textTransform: "none",
    borderRadius: 8,
    fontWeight: 600,
    minWidth: 96,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  listItemRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
}));

const QuickeMessageSchema = Yup.object().shape({
  shortcode: Yup.string().required("Obrigatório"),
});

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const messageInputRef = useRef();

  const initialState = {
    shortcode: "",
    message: "",
    geral: true,
    visao: true,
    queueId: null,
    isOficial: false,
    status: "",
    language: "",
    category: "",
    metaID: "",
    interactivePayload: null,
    interactiveType: "",
    interactiveButtons: [{ id: "1", title: "" }, { id: "2", title: "" }, { id: "3", title: "" }],
    interactiveButtonText: "Escolha uma opção",
    interactiveSections: [{ title: "Opções", rows: [{ id: "1", title: "" }] }],
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [quickemessage, setQuickemessage] = useState(initialState);
  const [queues, setQueues] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [mediaMode, setMediaMode] = useState(null); // 'file', 'audio', 'edit', null
  const [isEditingMedia, setIsEditingMedia] = useState(false);
  const attachmentFile = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Erro ao carregar filas:", err);
      }
    })();
  }, []);

  useEffect(() => {
    try {
      (async () => {
        if (!quickemessageId) return;

        const { data } = await api.get(`/quick-messages/${quickemessageId}`);

        setQuickemessage((prevState) => {
          const next = { ...prevState, ...initialState, ...data };
          if (data.interactivePayload && typeof data.interactivePayload === "string") {
            try {
              const parsed = JSON.parse(data.interactivePayload);
              next.interactiveType = parsed.type || "";
              if (parsed.type === "button" && Array.isArray(parsed.buttons)) {
                next.interactiveButtons = parsed.buttons.slice(0, 3).map((b, i) => ({ id: b.id || String(i + 1), title: b.title || "" }));
                while (next.interactiveButtons.length < 3) next.interactiveButtons.push({ id: String(next.interactiveButtons.length + 1), title: "" });
              }
              if (parsed.type === "list" && Array.isArray(parsed.sections)) {
                next.interactiveButtonText = parsed.buttonText || "Escolha uma opção";
                next.interactiveSections = parsed.sections.map((s) => ({ title: s.title || "Opções", rows: (s.rows || []).map((r, i) => ({ id: r.id || String(i + 1), title: r.title || "" })) }));
              }
            } catch (_) {
              next.interactiveType = "";
            }
          } else {
            next.interactiveType = "";
          }
          return next;
        });

        // Reset media editing state when loading existing message
        setIsEditingMedia(false);
        setMediaMode(null);
      })();
    } catch (err) {
      toastError(err);
    }
    // initialState é constante de montagem; não incluir para evitar loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickemessageId, open]);

  const handleClose = () => {
    setQuickemessage(initialState);
    setAttachment(null);
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
      setAudioBlob(null); // Limpar áudio se existir
      setMediaMode('file');
      setIsEditingMedia(true);
    }
  };

  const handleAudioRecorded = (blob) => {
    setAudioBlob(blob);
    setAttachment(null); // Limpar arquivo se existir
    setMediaMode('audio');
    setIsEditingMedia(true);
  };

  const handleAudioDeleted = () => {
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
  };

  const handleCancelEdit = () => {
    setAttachment(null);
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
    if (attachmentFile.current) {
      attachmentFile.current.value = null;
    }
  };

  const handleEditExistingMedia = () => {
    setIsEditingMedia(true);
    setMediaMode('edit');
  };

  const getBasename = (filepath) => {
    if (!filepath) return '';
    // Remove query strings e hashes
    const cleanPath = filepath.split('?')[0].split('#')[0];
    // Pega o último segmento após /
    const segments = cleanPath.split('/');
    return segments[segments.length - 1];
  };

  const handleSaveQuickeMessage = async (values) => {
    const interactivePayload =
      values.interactiveType === "button" && values.interactiveButtons?.filter((b) => b?.title?.trim()).length > 0
        ? JSON.stringify({
            type: "button",
            buttons: values.interactiveButtons
              .filter((b) => b?.title?.trim())
              .slice(0, 3)
              .map((b, i) => ({ id: b.id || String(i + 1), title: String(b.title).trim().substring(0, 20) })),
          })
        : values.interactiveType === "list" && values.interactiveSections?.some((s) => s?.rows?.length > 0)
          ? JSON.stringify({
              type: "list",
              buttonText: values.interactiveButtonText || "Escolha uma opção",
              sections: (values.interactiveSections || []).map((s) => ({
                title: (s.title || "Opções").substring(0, 24),
                rows: (s.rows || []).slice(0, 10).map((r, i) => ({
                  id: r.id || String(i + 1),
                  title: String(r.title || "").substring(0, 24),
                  description: r.description ? String(r.description).substring(0, 72) : undefined,
                })),
              })),
            })
          : null;

    const quickemessageData = {
      ...values,
      interactivePayload: interactivePayload || null,
      isMedia: !!(attachment || audioBlob || (quickemessage.mediaPath && !isEditingMedia)),
      mediaPath: attachment
        ? String(attachment.name).replace(/ /g, "_")
        : values.mediaPath
          ? getBasename(values.mediaPath).replace(/ /g, "_")
          : null,
      isOficial: quickemessageId ? values.isOficial : false,
    };

    try {
      let quickMessageRecord;

      if (quickemessageId) {
        quickMessageRecord = await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
      } else {
        const { data } = await api.post("/quick-messages", quickemessageData);
        quickMessageRecord = { data };
      }

      const messageId = quickemessageId || quickMessageRecord.data.id;

      // Se está editando mídia, primeiro remover a mídia antiga
      if (isEditingMedia && quickemessage.mediaPath) {
        await api.delete(`/quick-messages/${messageId}/media-upload`);
      }

      // Upload de arquivo comum
      if (attachment) {
        const formData = new FormData();
        formData.append("typeArch", "quickMessage"); // ✅ IMPORTANTE
        formData.append("file", attachment);

        console.log("📤 Enviando arquivo:", {
          name: attachment.name,
          type: attachment.type,
          size: attachment.size
        });

        await api.post(`/quick-messages/${messageId}/media-upload`, formData);
      }

      // ✅ CORREÇÃO: Upload de áudio gravado
      if (audioBlob) {
        const formData = new FormData();
        formData.append("typeArch", "quickMessage"); // ✅ IMPORTANTE para o multer
        const audioFile = new File([audioBlob], `audio_${Date.now()}.mp3`, {
          type: audioBlob.type || "audio/mpeg"
        });
        formData.append("audio", audioFile);

        console.log("🎵 Enviando áudio gravado:", {
          size: audioBlob.size,
          type: audioBlob.type
        });

        const response = await api.post(`/quick-messages/${messageId}/audio-upload`, formData);
        console.log("✅ Resposta do upload de áudio:", response.data);
      }

      toast.success(i18n.t("quickMessages.toasts.success"));
      if (typeof reload === "function") {
        reload();
      }
    } catch (err) {
      console.error("❌ Erro ao salvar quick message:", err);
      toastError(err);
    }
    handleClose();
  };

  const rowsWithIds = quickemessage?.components?.map((component, index) => ({
    id: index,
    ...component,
  }));

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (audioBlob) {
      setAudioBlob(null);
    }

    if (quickemessage.mediaPath) {
      await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
      setQuickemessage((prev) => ({
        ...prev,
        mediaPath: null,
        mediaName: null,
        mediaType: null
      }));
      toast.success(i18n.t("quickMessages.toasts.deleted"));
      if (typeof reload === "function") {
        reload();
      }
    }

    setMediaMode(null);
    setIsEditingMedia(false);
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;

    setValueFunc("message", `${firstHalfText}${msgVar}${secondHalfText}`);

    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const getMediaTypeIcon = (mediaType) => {
    switch (mediaType) {
      case 'audio': return '🎵';
      case 'image': return '🖼️';
      case 'video': return '🎥';
      default: return '📎';
    }
  };

  const getMediaPreview = (quickmessage) => {
    if (!quickmessage.mediaPath) return null;

    const mediaUrl = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}/public/company${user.companyId}/quickMessage/${quickmessage.mediaName}`;

    if (quickmessage.mediaType === 'image') {
      return (
        <img
          src={mediaUrl}
          alt={quickmessage.mediaName}
          className={classes.mediaPreview}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }

    //if (quickmessage.mediaType === 'audio') {
    //  return (
    //    <audio 
    //      controls 
    //      className={classes.mediaPreview}
    //      src={mediaUrl}
    //   >
    //      Seu navegador não suporta o elemento de áudio.
    //    </audio>
    //  );
    //}

    if (quickmessage.mediaType === 'video') {
      return (
        <video
          controls
          className={classes.mediaPreview}
          src={mediaUrl}
        >
          Seu navegador não suporta o elemento de vídeo.
        </video>
      );
    }

    return null;
  };

  // Verificar se há nova mídia sendo adicionada
  const hasNewMedia = attachment || audioBlob;
  // Verificar se há mídia existente (e não está sendo editada)
  const hasExistingMedia = quickemessage.mediaPath && !isEditingMedia;
  // Verificar se há qualquer tipo de mídia
  const hasAnyMedia = hasNewMedia || hasExistingMedia;

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("quickMessages.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <span className={classes.dialogTitleIcon}>
            <FlashOnIcon style={{ fontSize: 18 }} />
          </span>
          {quickemessageId
            ? `${i18n.t("quickMessages.dialog.edit")}`
            : `${i18n.t("quickMessages.dialog.add")}`}
        </DialogTitle>
        <div style={{ display: "none" }}>
          <input
            type="file"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={quickemessage}
          enableReinitialize={true}
          validationSchema={QuickeMessageSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickeMessage(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values }) => {
            const isDisabled = quickemessageId &&
              values.visao &&
              !values.geral &&
              values.userId !== user.id;

            return (
              <Form style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
                <DialogContent className={classes.dialogContent} dividers={false}>
                  <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    className={classes.tabs}
                  >
                    <Tab className={classes.tab} label={i18n.t("quickMessages.dialog.general")} />
                    {values.isOficial && <Tab className={classes.tab} label="Oficial" />}
                  </Tabs>
                  {tabIndex === 0 && (
                    <Grid spacing={1} container>
                      <Grid xs={12} item>
                        <Typography className={classes.sectionTitle}>
                          <ShortTextIcon className={classes.sectionIcon} />
                          {i18n.t("quickMessages.dialog.shortcode")}
                        </Typography>
                        <Field
                          as={TextField}
                          autoFocus
                          label={i18n.t("quickMessages.dialog.shortcode")}
                          name="shortcode"
                          disabled={isDisabled}
                          error={touched.shortcode && Boolean(errors.shortcode)}
                          helperText={touched.shortcode && errors.shortcode}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          className={classes.fieldDense}
                        />
                      </Grid>

                      <Grid xs={12} item>
                        <hr className={classes.sectionDivider} />
                        <Typography className={classes.sectionTitle}>
                          <MessageIcon className={classes.sectionIcon} />
                          {i18n.t("quickMessages.dialog.message")}
                        </Typography>
                        <Field
                          as={TextField}
                          label={i18n.t("quickMessages.dialog.message")}
                          name="message"
                          inputRef={messageInputRef}
                          error={touched.message && Boolean(errors.message)}
                          helperText={touched.message && errors.message}
                          variant="outlined"
                          margin="dense"
                          disabled={isDisabled}
                          multiline={true}
                          rows={6}
                          fullWidth
                          className={classes.fieldDense}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography className={classes.sectionTitle}>
                          <CodeIcon className={classes.sectionIcon} />
                          {i18n.t("messageVariablesPicker.label")}
                        </Typography>
                        <MessageVariablesPicker
                          disabled={isSubmitting || isDisabled}
                          showSchedulingVars={true}
                          hideLabel
                          onClick={(value) =>
                            handleClickMsgVar(value, setFieldValue)
                          }
                        />
                      </Grid>

                      <Grid xs={12} item>
                        <hr className={classes.sectionDivider} />
                        <Typography className={classes.sectionTitle}>
                          <WhatsAppIcon className={classes.sectionIcon} />
                          Enviar como botões/lista no WhatsApp
                        </Typography>
                        <Box className={classes.sectionCard}>
                          <FormControl
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.fieldDense}
                          >
                            <InputLabel>Tipo</InputLabel>
                            <Select
                              value={values.interactiveType || ""}
                              onChange={(e) => setFieldValue("interactiveType", e.target.value)}
                              label="Tipo"
                            >
                              <MenuItem value="">Nenhum (só texto)</MenuItem>
                              <MenuItem value="button">Botões (até 3)</MenuItem>
                              <MenuItem value="list">Lista</MenuItem>
                            </Select>
                          </FormControl>
                          {values.interactiveType === "button" && (
                            <Box>
                              {(values.interactiveButtons || []).slice(0, 3).map((btn, i) => (
                                <TextField
                                  key={i}
                                  label={`Botão ${i + 1}`}
                                  value={btn?.title || ""}
                                  onChange={(e) => {
                                    const next = [...(values.interactiveButtons || [])];
                                    while (next.length < 3) next.push({ id: String(next.length + 1), title: "" });
                                    next[i] = { ...next[i], id: next[i].id || String(i + 1), title: e.target.value };
                                    setFieldValue("interactiveButtons", next);
                                  }}
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  className={classes.fieldDense}
                                  inputProps={{ maxLength: 20 }}
                                  helperText="Máx. 20 caracteres"
                                />
                              ))}
                            </Box>
                          )}
                          {values.interactiveType === "list" && (
                            <Box>
                              <TextField
                                label="Texto do botão que abre a lista"
                                value={values.interactiveButtonText || "Escolha uma opção"}
                                onChange={(e) => setFieldValue("interactiveButtonText", e.target.value)}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                className={classes.fieldDense}
                              />
                              <Typography variant="body2" style={{ marginTop: 8, marginBottom: 4, fontWeight: 600 }}>
                                Itens da lista (máx. 10)
                              </Typography>
                              {(values.interactiveSections?.[0]?.rows || []).map((row, i) => (
                                <Box key={i} className={classes.listItemRow}>
                                  <TextField
                                    label={`Item ${i + 1}`}
                                    value={row?.title || ""}
                                    onChange={(e) => {
                                      const sec = values.interactiveSections?.[0] || { title: "Opções", rows: [] };
                                      const rows = [...(sec.rows || [])];
                                      while (rows.length <= i) rows.push({ id: String(rows.length + 1), title: "" });
                                      rows[i] = { ...rows[i], id: rows[i].id || String(i + 1), title: e.target.value };
                                      setFieldValue("interactiveSections", [{ ...sec, rows }]);
                                    }}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    className={classes.fieldDense}
                                    inputProps={{ maxLength: 24 }}
                                  />
                                  {(values.interactiveSections?.[0]?.rows || []).length > 1 && (
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const sec = values.interactiveSections?.[0] || { title: "Opções", rows: [] };
                                        const rows = (sec.rows || []).filter((_, j) => j !== i);
                                        setFieldValue("interactiveSections", [{ ...sec, rows: rows.length ? rows : [{ id: "1", title: "" }] }]);
                                      }}
                                      title="Remover item"
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              ))}
                              <Button
                                size="small"
                                color="primary"
                                className={classes.mediaOptionBtn}
                                onClick={() => {
                                  const sec = values.interactiveSections?.[0] || { title: "Opções", rows: [] };
                                  const currentRows = sec.rows || [];
                                  const rows = [...currentRows, { id: String(currentRows.length + 1), title: "" }];
                                  if (rows.length <= 10) setFieldValue("interactiveSections", [{ ...sec, rows }]);
                                }}
                                disabled={(values.interactiveSections?.[0]?.rows || []).length >= 10}
                              >
                                + Adicionar item
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Grid>

                      <Grid xs={12} item>
                        <hr className={classes.sectionDivider} />
                        <Typography className={classes.sectionTitle}>
                          <AttachFileIcon className={classes.sectionIcon} />
                          Anexar Mídia
                        </Typography>
                        <Box className={classes.sectionCard}>
                          {hasExistingMedia && (
                            <>
                              <Box className={classes.mediaInfo}>
                                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                  <span>{getMediaTypeIcon(quickemessage.mediaType)}</span>
                                  <Typography variant="body2">
                                    {quickemessage.mediaName}
                                  </Typography>
                                  {quickemessage.mediaType && (
                                    <Chip
                                      size="small"
                                      label={quickemessage.mediaType}
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                                <Box className={classes.existingMediaActions}>
                                  <IconButton
                                    onClick={handleEditExistingMedia}
                                    color="primary"
                                    size="small"
                                    title="Editar mídia"
                                    disabled={isDisabled}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => setConfirmationOpen(true)}
                                    color="secondary"
                                    size="small"
                                    title="Remover mídia"
                                    disabled={isDisabled}
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                              {getMediaPreview(quickemessage)}
                            </>
                          )}

                          {attachment && (
                            <Box className={classes.mediaInfo}>
                              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <AttachFileIcon fontSize="small" />
                                <Typography variant="body2">
                                  {attachment.name}
                                </Typography>
                                <Chip size="small" label="Novo Arquivo" color="primary" />
                              </Box>
                              <IconButton
                                onClick={() => {
                                  setAttachment(null);
                                  setMediaMode(null);
                                  setIsEditingMedia(false);
                                  if (attachmentFile.current) {
                                    attachmentFile.current.value = null;
                                  }
                                }}
                                color="secondary"
                                size="small"
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Box>
                          )}

                          {audioBlob && (
                            <Box className={classes.mediaInfo}>
                              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <MicIcon fontSize="small" />
                                <Typography variant="body2">
                                  Novo áudio gravado
                                </Typography>
                                <Chip size="small" label="Novo Áudio" color="secondary" />
                              </Box>
                              <IconButton
                                onClick={handleAudioDeleted}
                                color="secondary"
                                size="small"
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Box>
                          )}

                          {(!hasAnyMedia || isEditingMedia) && (
                            <>
                              {isEditingMedia && hasExistingMedia && (
                                <>
                                  <Divider />
                                  <Typography variant="body2" color="textSecondary">
                                    Escolha uma nova mídia para substituir:
                                  </Typography>
                                </>
                              )}

                              {!hasAnyMedia && (
                                <Typography variant="body2" color="textSecondary">
                                  Escolha uma opção para anexar mídia:
                                </Typography>
                              )}

                              <Box className={classes.mediaOptions}>
                                <Button
                                  variant="outlined"
                                  startIcon={<AttachFileIcon />}
                                  onClick={() => attachmentFile.current.click()}
                                  disabled={isSubmitting || isDisabled}
                                  className={classes.mediaOptionBtn}
                                >
                                  Anexar Arquivo
                                </Button>
                                <Button
                                  variant="outlined"
                                  startIcon={<MicIcon />}
                                  onClick={() => setMediaMode('audio')}
                                  disabled={isSubmitting || isDisabled}
                                  className={classes.mediaOptionBtn}
                                >
                                  Gravar Áudio
                                </Button>

                                {isEditingMedia && (
                                  <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleCancelEdit}
                                    disabled={isSubmitting}
                                    className={classes.mediaOptionBtn}
                                  >
                                    Cancelar Edição
                                  </Button>
                                )}
                              </Box>
                            </>
                          )}

                          {mediaMode === 'audio' && !audioBlob && (
                            <AudioRecorder
                              onAudioRecorded={handleAudioRecorded}
                              onAudioDeleted={handleAudioDeleted}
                              disabled={isSubmitting || isDisabled}
                            />
                          )}
                        </Box>
                      </Grid>

                      <Grid xs={12} item>
                        <hr className={classes.sectionDivider} />
                        <Typography className={classes.sectionTitle}>
                          <VisibilityIcon className={classes.sectionIcon} />
                          Disponibilidade / Escopo
                        </Typography>
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          className={classes.fieldDense}
                        >
                          <InputLabel id="scope-selection-label">
                            Quem pode ver e usar esta resposta?
                          </InputLabel>
                          <Select
                            label="Quem pode ver e usar esta resposta?"
                            labelId="scope-selection-label"
                            id="scope-select"
                            disabled={isDisabled}
                            value={
                              values.geral === true
                                ? "global"
                                : values.queueId
                                ? "queue"
                                : "user"
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "global") {
                                setFieldValue("geral", true);
                                setFieldValue("visao", true);
                                setFieldValue("queueId", null);
                              } else if (val === "queue") {
                                setFieldValue("geral", false);
                                setFieldValue("visao", true);
                                setFieldValue(
                                  "queueId",
                                  values.queueId || (queues.length > 0 ? queues[0].id : null)
                                );
                              } else {
                                setFieldValue("geral", false);
                                setFieldValue("visao", true);
                                setFieldValue("queueId", null);
                              }
                            }}
                          >
                            <MenuItem value="global">
                              🌐 Global (Toda a Empresa)
                            </MenuItem>
                            <MenuItem value="queue">
                              👥 Por Setor / Fila
                            </MenuItem>
                            <MenuItem value="user">
                              👤 Pessoal (Apenas Eu)
                            </MenuItem>
                          </Select>
                        </FormControl>

                        {!values.geral && (
                          <FormControl
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.fieldDense}
                            style={{ marginTop: 8 }}
                          >
                            <InputLabel id="queue-selection-label">
                              Selecione o Setor / Fila
                            </InputLabel>
                            <Select
                              label="Selecione o Setor / Fila"
                              labelId="queue-selection-label"
                              id="queue-select"
                              disabled={isDisabled}
                              value={values.queueId || ""}
                              onChange={(e) => setFieldValue("queueId", e.target.value || null)}
                            >
                              <MenuItem value="">
                                <em>Nenhuma (Pessoal / Apenas Eu)</em>
                              </MenuItem>
                              {queues.map((q) => (
                                <MenuItem key={q.id} value={q.id}>
                                  <span style={{ display: "flex", alignItems: "center" }}>
                                    <span
                                      style={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: "50%",
                                        backgroundColor: q.color || "#888",
                                        marginRight: 8,
                                        display: "inline-block",
                                      }}
                                    />
                                    {q.name}
                                  </span>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Grid>
                    </Grid>
                  )}
                  {tabIndex === 1 && (
                    <>
                      <Grid xs={12} item>
                        <DataGrid
                          rows={rowsWithIds}
                          columns={[
                            { field: "type", headerName: "Tipo", width: 150 },
                            { field: "text", headerName: "Valor", width: 400 },
                          ]}
                          pageSize={5}
                          disableSelectionOnClick
                          autoHeight={true}
                        />
                      </Grid>
                      <Grid container spacing={2}>
                        <Grid xl={6} md={6} sm={12} xs={12} item>
                          <Field
                            as={TextField}
                            label={i18n.t("quickMessages.dialog.status")}
                            name="status"
                            disabled={values.isOficial}
                            error={touched.status && Boolean(errors.status)}
                            helperText={touched.status && errors.status}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.fieldDense}
                          />
                        </Grid>
                        <Grid xl={6} md={6} sm={12} xs={12} item>
                          <Field
                            as={TextField}
                            label={i18n.t("quickMessages.dialog.language")}
                            name="language"
                            disabled={values.isOficial}
                            error={touched.language && Boolean(errors.language)}
                            helperText={touched.language && errors.language}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.fieldDense}
                          />
                        </Grid>
                        <Grid xl={6} md={6} sm={12} xs={12} item>
                          <Field
                            as={TextField}
                            label={i18n.t("quickMessages.dialog.category")}
                            name="category"
                            disabled={values.isOficial}
                            error={touched.category && Boolean(errors.category)}
                            helperText={touched.category && errors.category}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.fieldDense}
                          />
                        </Grid>
                        <Grid xl={6} md={6} sm={12} xs={12} item>
                          <Field
                            as={TextField}
                            label={i18n.t("quickMessages.dialog.metaID")}
                            name="metaID"
                            disabled={values.isOficial}
                            error={touched.metaID && Boolean(errors.metaID)}
                            helperText={touched.metaID && errors.metaID}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.fieldDense}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                  <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    variant="outlined"
                    className={classes.cancelBtn}
                  >
                    {i18n.t("quickMessages.buttons.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting || isDisabled}
                    variant="contained"
                    disableElevation
                    className={`${classes.btnWrapper} ${classes.saveBtn}`}
                  >
                    {quickemessageId
                      ? `${i18n.t("quickMessages.buttons.edit")}`
                      : `${i18n.t("quickMessages.buttons.add")}`}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QuickMessageDialog;
